import React, { useMemo, useState } from 'react';
import { MenuIcon, ChevronRightIcon, RocketIcon, GaugeIcon, WindIcon, ZapOffIcon, EyeIcon } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PortalBrandSwitcher from './PortalBrandSwitcher';
import NotificationsBell from './NotificationsBell';
import MessagesMenu from './MessagesMenu';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { isV2PreviewEnabled } from '../../utils/featureFlags';
import { TOPBAR_AGENT_SLOT_ID, TOPBAR_SLOT_ID } from '../v2/topBarSlots';
import NavbarV2 from './NavbarV2';
import { useV2Shell } from './useV2Shell';

interface NavbarProps {
  toggleSidebar: () => void;
}

// Breadcrumb segment interface
interface BreadcrumbSegment {
  label: string;
  path?: string;
  onClick?: () => void;
}

// Function to build Intelligence Suite breadcrumbs
const buildIntelligenceSuiteBreadcrumbs = (
  pathname: string,
  searchParams: URLSearchParams,
  locationState: any,
  styleId?: string
): BreadcrumbSegment[] => {
  const breadcrumbs: BreadcrumbSegment[] = [
    { label: 'Intelligence Suite', path: '/portal' }
  ];

  // Get breadcrumb context from navigation state
  const breadcrumbContext = locationState?.breadcrumb;

  // Check if we're in a specific view
  const segments = pathname.split('/').filter(Boolean);
  const subRoute = segments[1];

  // Handle Total View
  if (subRoute === 'total') {
    breadcrumbs.push({ label: 'Total View', path: '/portal/total' });

    // Add active tab if present
    const activeTab = searchParams.get('tab');
    if (activeTab) {
      const tabLabels: { [key: string]: string } = {
        sales: 'Sales',
        inventory: 'Inventory',
        returns: 'Returns',
        promotions: 'Promotions'
      };
      breadcrumbs.push({
        label: tabLabels[activeTab] || activeTab,
        path: `/portal/total?tab=${activeTab}`
      });
    }
  }
  // Handle Style Detail View
  else if (subRoute === 'style' && styleId) {
    // Add source view (Total View or Style View)
    if (breadcrumbContext?.fromView === 'total') {
      breadcrumbs.push({ label: 'Total View', path: '/portal/total' });

      // Add the tab they came from
      const activeTab = breadcrumbContext?.activeTab || searchParams.get('tab') || 'sales';
      const tabLabels: { [key: string]: string } = {
        sales: 'Sales',
        inventory: 'Inventory',
        returns: 'Returns',
        promotions: 'Promotions'
      };
      breadcrumbs.push({
        label: tabLabels[activeTab] || activeTab,
        path: `/portal/total?tab=${activeTab}`
      });
    } else {
      breadcrumbs.push({ label: 'Style View', path: '/portal' });
    }

    // Get style info
    const styleName = breadcrumbContext?.styleName || 'Style';

    // Add style name
    breadcrumbs.push({
      label: styleName,
      path: `/portal/style/${styleId}`
    });

    // Add color if present
    const color = searchParams.get('color');
    if (color) {
      breadcrumbs.push({
        label: color,
        path: `/portal/style/${styleId}?color=${color}`
      });

      // Add size if present
      const size = searchParams.get('size');
      if (size) {
        breadcrumbs.push({
          label: size
          // No path for the current level
        });
      }
    }
  }
  // Handle AI Insights
  else if (subRoute === 'ai-insights') {
    breadcrumbs.push({ label: 'AI Insights', path: '/portal/ai-insights' });
  }
  // Style View (catalog)
  else if (pathname === '/portal') {
    breadcrumbs.push({ label: 'Style View' });
  }

  return breadcrumbs;
};

// Function to get page name from route (for non-Intelligence Suite pages)
const getPageName = (pathname: string): string => {
  // Handle root path
  if (pathname === '/') return 'Home';

  // Split pathname into segments
  const segments = pathname.split('/').filter(Boolean);
  const mainRoute = segments[0];
  const subRoute = segments[1];

  // Main route mappings
  const routeMap: { [key: string]: string } = {
    'overview': 'Home',
    'performance': 'Performance',
    'inventory': 'Inventory',
    'sales': 'Sales',
    'returns': 'Returns',
    'marketing': 'Marketing',
    'marketplace': 'Marketplaces',
    'my-reports': 'My Reports',
    'reconcile': 'Reconcile',
    'catalog': 'Catalog',
    'recommend': 'Recommend',
    'addons': 'Add-ons',
    'profile': 'Profile',
    'onboarding': 'Brand Onboarding',
    'notifications': 'Notifications',
    'style': 'Style Details'
  };

  // Sub-route mappings for other sections
  if (subRoute === 'ai-insights') {
    return `${routeMap[mainRoute] || mainRoute} - AI Insights`;
  }

  return routeMap[mainRoute] || mainRoute.charAt(0).toUpperCase() + mainRoute.slice(1);
};

const NavbarV1: React.FC<NavbarProps> = ({ toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useCurrentUser();

  // Check if we're in Intelligence Suite
  const isIntelligenceSuite = location.pathname.startsWith('/portal');

  // Extract style ID from pathname if present
  const styleIdMatch = location.pathname.match(/\/portal\/style\/([^/]+)/);
  const styleId = styleIdMatch ? styleIdMatch[1] : undefined;

  // Build breadcrumbs or get page name
  const breadcrumbs = useMemo(() => {
    if (isIntelligenceSuite) {
      return buildIntelligenceSuiteBreadcrumbs(
        location.pathname,
        searchParams,
        location.state,
        styleId
      );
    }
    return null;
  }, [location.pathname, searchParams, location.state, styleId, isIntelligenceSuite]);

  const pageName = !isIntelligenceSuite ? getPageName(location.pathname) : null;

  // Health indicator hover state
  const [hoveredStatus, setHoveredStatus] = useState<string | null>(null);

  // Health indicator data
  const statusIndicators = [
    { key: 'rocket', icon: RocketIcon, color: 'text-indicator-rocket', borderColor: 'border-indicator-rocket', glowColor: 'shadow-indicator-rocket/50', label: 'Rocket', description: 'High velocity' },
    { key: 'cruiser', icon: GaugeIcon, color: 'text-indicator-cruiser', borderColor: 'border-indicator-cruiser', glowColor: 'shadow-indicator-cruiser/50', label: 'Cruiser', description: 'Optimal' },
    { key: 'fading', icon: WindIcon, color: 'text-indicator-fading', borderColor: 'border-indicator-fading', glowColor: 'shadow-indicator-fading/50', label: 'Fading', description: 'Declining' },
    { key: 'stalled', icon: ZapOffIcon, color: 'text-indicator-stalled', borderColor: 'border-indicator-stalled', glowColor: 'shadow-indicator-stalled/50', label: 'Stalled', description: 'Critical' },
    { key: 'discovery', icon: EyeIcon, color: 'text-indicator-discovery', borderColor: 'border-indicator-discovery', glowColor: 'shadow-indicator-discovery/50', label: 'Discovery', description: 'Observing' }
  ];

  return (
    <header className="bg-transparent border-b border-black/5 z-10" style={{ minHeight: isIntelligenceSuite ? '80px' : '64px' }}>
      <div className="flex flex-col h-full">
        {/* Main navbar row */}
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center min-w-0 flex-1">
            <button
              onClick={toggleSidebar}
              className="text-gray-600 focus:outline-none lg:hidden flex-shrink-0"
            >
              <MenuIcon size={24} />
            </button>

            {/* Brand name + page title */}
            {/* Non-portal pages baseline-align (not center) so the caps brand name and
                lowercase page name share a text baseline; box-centering sat the caps too high. */}
            <div
              className={`ml-4 lg:ml-0 flex ${breadcrumbs ? 'items-center' : 'items-baseline'} gap-2 min-w-0 flex-1`}
            >
              <PortalBrandSwitcher />
              {breadcrumbs && (
                <span className="text-gray-300">|</span>
              )}
              {/* Intelligence Suite Breadcrumbs */}
              {breadcrumbs ? (
                <nav className="flex items-center gap-2 min-w-0 overflow-x-auto">
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                      {crumb.path ? (
                        <button
                          onClick={() => navigate(crumb.path!)}
                          className={`${
                            index === 0
                              ? 'text-lg font-bold text-gray-900 hover:text-indigo-600'
                              : 'text-sm text-gray-600 hover:text-indigo-600'
                          } transition-colors whitespace-nowrap`}
                        >
                          {crumb.label}
                        </button>
                      ) : (
                        <span className={`${
                          index === 0
                            ? 'text-lg font-bold text-gray-900'
                            : 'text-sm text-gray-600'
                        } whitespace-nowrap`}>
                          {crumb.label}
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </nav>
              ) : (
                /* Regular page name for non-Intelligence Suite pages — the V2 page masthead owns the page title */
                <>
                  <span className="self-center text-[8px] leading-none text-gray-300 select-none" aria-hidden>
                    •
                  </span>
                  <span className="text-sm font-medium leading-none text-gray-600">{pageName}</span>
                </>
              )}
            </div>
          </div>

          {/* V2 top-bar slots (always in the DOM, empty on v1 pages): a v2 page's
              tabs + freshness stamp portal into the first (components/v2/TopBar),
              AdvisorDock's "Ask the agent" into the second (ruling 10). */}
          <div id={TOPBAR_SLOT_ID} className="ml-3 flex min-w-0 justify-end empty:hidden" />
          <div className="ml-3 flex items-center gap-4 flex-shrink-0">
            <div id={TOPBAR_AGENT_SLOT_ID} className="contents" />
            {/* Messages — notices from Global Bridge (rate card). Every brand user; hides itself when nothing applies. */}
            <MessagesMenu />
            {/* Notifications — V2 preview surface (master plan Amendment 2026-07-09). */}
            {isV2PreviewEnabled(user) && (
              <div className="relative">
                <NotificationsBell />
              </div>
            )}
          </div>
        </div>

        {/* Health Indicators - Only show in Intelligence Suite */}
        {isIntelligenceSuite && (
          <div className="flex items-center gap-4 px-4 pt-3 pb-2 border-t border-gray-100">
            <span className="text-[10px] font-medium text-gray-500">Health:</span>
            <div className="flex items-center gap-3">
              {statusIndicators.map((status, index) => (
                <React.Fragment key={status.key}>
                  <div
                    className="relative"
                    onMouseEnter={() => setHoveredStatus(status.key)}
                    onMouseLeave={() => setHoveredStatus(null)}
                  >
                    <div className="flex items-center gap-1.5 cursor-default">
                      <status.icon className={`h-3.5 w-3.5 ${status.color}`} />
                      <div className={`w-4 h-4 rounded border-2 ${status.borderColor} shadow-md ${status.glowColor}`}></div>
                    </div>

                    {/* Dropdown tooltip */}
                    {hoveredStatus === status.key && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap z-50">
                        <div className="font-medium">{status.label}</div>
                        <div className="text-gray-300">{status.description}</div>
                        {/* Arrow pointing up */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
                      </div>
                    )}
                  </div>
                  {/* Divider between indicators */}
                  {index < statusIndicators.length - 1 && (
                    <div className="h-5 w-px bg-gray-300"></div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <span className="text-[10px] text-gray-400">Border colors on variants match health levels</span>
          </div>
        )}
      </div>
    </header>
  );
};

/**
 * The frame's top bar. v2 shell users get the mock's top bar (NavbarV2),
 * except inside the Intelligence Suite, whose breadcrumbs and health legend
 * only the v1 bar draws. Every v1 user gets today's bar.
 */
const Navbar: React.FC<NavbarProps> = (props) => {
  const v2 = useV2Shell();
  const inSuite = useLocation().pathname.startsWith('/portal');
  return v2 && !inSuite ? <NavbarV2 {...props} /> : <NavbarV1 {...props} />;
};

export default Navbar;